/** 登记文件单文件大小上限，20MB。 */
export const ANAGRAFICA_OCR_MAX_BYTES = 20 * 1024 * 1024;

/** 高置信阈值，与后端 high 档一致。 */
const HIGH_CONFIDENCE = 0.92;

/** 中置信下限，低于该值视为低置信。 */
const MID_CONFIDENCE = 0.75;

/** 允许上传的扩展名。 */
const OCR_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.heif'];

/** 允许上传的 MIME。空 MIME 时改看扩展名。 */
const OCR_FILE_MIME_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/heic',
    'image/heif',
];

/**
 * 新增餐厅允许被登记文件写入的字段。
 * 附录 A 中负责人、商务配置、会计师等不予填充。
 */
export const RESTAURANT_OCR_FILLABLE_KEYS = [
    'nickname',
    'reg_name',
    'reg_fiscal_code',
    'reg_vat_number',
    'reg_pec',
    'reg_des_code',
    'reg_local',
    'reg_postcode',
    'reg_city',
    'reg_state',
    'reg_country',
    'local',
    'postcode',
    'city',
    'state',
    'country',
    'restaurant_contact_person',
] as const;

/** 可填充字段集合，用于忽略白名单外的识别结果。 */
const FILLABLE_KEY_SET = new Set<string>(RESTAURANT_OCR_FILLABLE_KEYS);

/**
 * 双地址自定义块展开后参与分区「AI 填充」计数的字段。
 * 与 CardFrom 中 `dual_address` 实际可挂 OCR 的地址字段对齐。
 */
export const RESTAURANT_OCR_DUAL_ADDRESS_KEYS = [
    'local',
    'postcode',
    'city',
    'state',
    'country',
    'reg_local',
    'reg_postcode',
    'reg_city',
    'reg_state',
    'reg_country',
] as const;

/** 识别字段与现值的关系。 */
export type AnagraficaOcrFieldStatus = 'fill' | 'match' | 'conflict';

/** 置信度分档。 */
export type AnagraficaOcrConfidenceLevel = 'high' | 'mid' | 'low';

/** 保存确认时采纳的一侧：识别值、系统现值，或用户手改第三值。 */
export type AnagraficaOcrChosen = 'ocr' | 'current' | 'manual';

/**
 * 原件上的文字区域，原点为页面/图片左上角，y 向下。
 * PDF：页内坐标，单位 pt；图片（JPG/PNG/HEIC）：相对整张原图宽高的 0–1 小数。
 */
export interface AnagraficaOcrBBox {
    /** 距左边缘（PDF 为 pt；图片为相对宽度 0–1）。 */
    x: number;
    /** 距上边缘（PDF 为 pt；图片为相对高度 0–1）。 */
    y: number;
    /** 区域宽度（PDF 为 pt；图片为相对宽度 0–1）。 */
    w: number;
    /** 区域高度（PDF 为 pt；图片为相对高度 0–1）。 */
    h: number;
}

/** parse 接口返回的单个字段。 */
export interface AnagraficaOcrApiField {
    /** 餐厅实体字段名。 */
    field_id: string;
    /** 文件上的原文标签。 */
    source_field?: string;
    /** 识别原值。 */
    value_ocr?: string;
    /** 会话当前准备采用的值。 */
    value_confirmed?: string;
    /** 解析时的系统现值；新建通常为空。 */
    current_value?: string;
    /** 0 到 1 的置信度。 */
    confidence?: number;
    /** 后端分档；缺失时按阈值本地计算。 */
    confidence_level?: string;
    /** PDF 页码，从 1 开始。 */
    page?: number;
    /** 原件高亮区域；PDF 为 pt，图片为 0–1 相对坐标，缺失时为 null。 */
    bbox?: AnagraficaOcrBBox | null;
    /** fill、match 或 conflict。 */
    status?: string;
    /** 后端是否已视为核对完成。 */
    confirmed?: boolean;
    /** 采纳侧；列表/留痕回显时可能有 manual。 */
    chosen?: AnagraficaOcrChosen | null;
    /** 核对人账号 _id。 */
    confirmed_by?: string;
    /** 核对时间。 */
    confirmed_at?: string;
}

/** parse 接口成功体。 */
export interface AnagraficaOcrParseResult {
    /** OCR 会话 ID。 */
    session_id: string;
    /** 原始文件名。 */
    file_name?: string;
    /**
     * 登记文件签发国家短码（如 ES / IT / FR）。
     * 用于写入建档「所在国家」主字段 `country_code` 及两侧地址国家。
     */
    country?: string;
    /** 税务地址与营业地址是否视为同一套。 */
    same_addr?: boolean;
    /** 识别出的字段。 */
    fields?: AnagraficaOcrApiField[];
}

/**
 * 登记文件签发国家 → 所在国家电话区号。
 * 与当前 parse 支持的 ES / IT / FR 对齐。
 */
export const OCR_DOCUMENT_COUNTRY_PHONE_CODES: Record<string, string> = {
    IT: '+39',
    ES: '+34',
    FR: '+33',
};

/**
 * 将 parse 顶层 `country` 写入所在国家主字段及两侧只读地址国家。
 * 未知或空值时原样返回，不覆盖表单已有国家。
 * @param data 餐厅表单副本
 * @param documentCountry 后端签发国家短码
 */
export function applyOcrDocumentCountry<T extends Record<string, unknown>>(
    data: T,
    documentCountry?: string | null,
): T {
    /** 规范化后的签发国家短码（大写）。 */
    const normalized = String(documentCountry ?? '').trim().toUpperCase();
    if (!normalized) {
        return data;
    }
    /** 短码对应的电话区号；不在支持列表内则忽略。 */
    const phoneCode = OCR_DOCUMENT_COUNTRY_PHONE_CODES[normalized];
    if (!phoneCode) {
        return data;
    }
    return {
        ...data,
        country_code: phoneCode,
        country: normalized,
        reg_country: normalized,
    };
}

/** 前端核对中的单个字段。 */
export interface AnagraficaOcrReviewField {
    /** 餐厅实体字段名。 */
    fieldId: string;
    /** 文件上的原文标签。 */
    sourceField: string;
    /** 识别原值，人工修改后仍保留。 */
    valueOcr: string;
    /** 解析时建议写入的值。 */
    valueConfirmed: string;
    /** 系统现值。 */
    currentValue: string;
    /** 0 到 1 的置信度。 */
    confidence: number;
    /** 置信度分档。 */
    level: AnagraficaOcrConfidenceLevel;
    /** 与现值的关系。 */
    status: AnagraficaOcrFieldStatus;
    /** 所在页码。 */
    page: number;
    /** 原件高亮区域。 */
    bbox: AnagraficaOcrBBox | null;
    /** 用户是否已核对。 */
    confirmed: boolean;
    /** 用户是否改过表单值。 */
    modified: boolean;
    /** 是否由当前仍有效的批量确认标为已核对。 */
    confirmedByBatch: boolean;
    /** 是否由用户点印章或采用识别值完成核对（不含 match 自动与批量）。 */
    confirmedManually: boolean;
    /** 采纳识别值、系统现值，或用户手改第三值。 */
    chosen: AnagraficaOcrChosen | null;
}

/** 新增餐厅的前端 OCR 核对会话。 */
export interface AnagraficaOcrReviewSession {
    /** OCR 会话 ID。 */
    sessionId: string;
    /** 展示用文件名。 */
    fileName: string;
    /** 是否勾选「两个地址相同」。 */
    sameAddr: boolean;
    /** 批量确认仍有效时，保存才调用 confirm-high，可再点撤销。 */
    confirmHighArmed: boolean;
    /** 本轮批量确认覆盖的字段（未改写，保存走 confirm-high）。 */
    batchFieldIds: string[];
    /** 可填充且需要核对的字段。 */
    fields: AnagraficaOcrReviewField[];
    /** 原件与表单当前对位的字段。 */
    activeFieldId: string | null;
}

/** confirm 接口单条字段载荷。 */
export interface AnagraficaOcrConfirmFieldPayload {
    /** 餐厅字段名。 */
    field_id: string;
    /** 采纳识别值、系统现值，或用户手改第三值。 */
    chosen: AnagraficaOcrChosen;
    /** chosen 为 manual 时必传：输入框最终字符串。 */
    value?: string;
}

/** 保存前要提交的确认计划。 */
export interface AnagraficaOcrSavePlan {
    /** 是否调用 confirm-high。 */
    callConfirmHigh: boolean;
    /** 用户手动核对字段及采纳侧（不含批量与 match 自动项）。 */
    fields: AnagraficaOcrConfirmFieldPayload[];
}

/** 上传文件校验结果。 */
export type AnagraficaOcrFileCheck = 'ok' | 'type' | 'size';

/**
 * 判断字段是否允许被登记文件写入。
 * @param fieldId 识别结果里的字段名
 */
export function isRestaurantOcrFillableKey(fieldId: string): boolean {
    return FILLABLE_KEY_SET.has(fieldId);
}

/**
 * 收集建档分区内可参与 OCR 计数的字段名。
 * `dual_address` 展开为两侧地址字段，其余取卡片子项 key。
 * @param children 分区 CardFrom 子项
 */
export function collectRestaurantSectionOcrKeys(
    children: ReadonlyArray<{ key: string }>,
): string[] {
    return children.flatMap((child) => (
        child.key === 'dual_address'
            ? [...RESTAURANT_OCR_DUAL_ADDRESS_KEYS]
            : [child.key]
    ));
}

/**
 * 统计某分区有多少识别字段被写入。
 * @param fields OCR 核对字段
 * @param sectionKeys 该分区字段名
 */
export function countOcrFieldsForSectionKeys(
    fields: ReadonlyArray<Pick<AnagraficaOcrReviewField, 'fieldId'>>,
    sectionKeys: readonly string[],
): number {
    /** 分区字段集合，用于 O(1) 判断。 */
    const keySet = new Set(sectionKeys);
    return fields.filter((field) => keySet.has(field.fieldId)).length;
}

/**
 * 把后端分档或置信度收成三档。
 * @param confidence 0 到 1
 * @param levelFromApi 后端 confidence_level
 */
export function resolveOcrConfidenceLevel(
    confidence: number,
    levelFromApi?: string,
): AnagraficaOcrConfidenceLevel {
    if (levelFromApi === 'high' || levelFromApi === 'mid' || levelFromApi === 'low') {
        return levelFromApi;
    }
    if (confidence >= HIGH_CONFIDENCE) {
        return 'high';
    }
    if (confidence >= MID_CONFIDENCE) {
        return 'mid';
    }
    return 'low';
}

/**
 * 校验登记文件类型与大小。
 * @param file 用户选择的本地文件
 */
export function validateAnagraficaOcrFile(file: { name: string; type: string; size: number }): AnagraficaOcrFileCheck {
    if (file.size > ANAGRAFICA_OCR_MAX_BYTES) {
        return 'size';
    }
    const lowerName = file.name.toLowerCase();
    const extensionOk = OCR_FILE_EXTENSIONS.some((extension) => lowerName.endsWith(extension));
    const mimeOk = OCR_FILE_MIME_TYPES.includes(file.type);
    if (!extensionOk && !mimeOk) {
        return 'type';
    }
    return 'ok';
}

/**
 * 归一化比较用文本：去首尾空白，连续空白压成单空格。
 * @param value 原始文本
 */
function normalizeOcrCompareText(value: string): string {
    return String(value).trim().replace(/\s+/g, ' ');
}

/**
 * 按表单文本决定确认接口的 chosen。
 * 等于识别值 → ocr；等于非空系统现值 → current；否则 → manual（须另传 value）。
 * @param field 核对字段
 * @param formValue 当前表单文本
 */
export function resolveOcrChosen(
    field: Pick<AnagraficaOcrReviewField, 'valueOcr' | 'currentValue' | 'status'>,
    formValue: string,
): AnagraficaOcrChosen {
    const normalizedForm = normalizeOcrCompareText(formValue);
    if (normalizedForm === normalizeOcrCompareText(field.valueOcr)) {
        return 'ocr';
    }
    const normalizedCurrent = normalizeOcrCompareText(field.currentValue);
    if (normalizedCurrent !== '' && normalizedForm === normalizedCurrent) {
        return 'current';
    }
    return 'manual';
}

/**
 * 构造 confirm 接口单条字段：manual 时附带输入框最终字符串。
 * @param field 核对字段
 * @param formValue 当前表单文本
 */
export function buildOcrConfirmFieldPayload(
    field: Pick<AnagraficaOcrReviewField, 'fieldId' | 'valueOcr' | 'currentValue' | 'status'>,
    formValue: string,
): AnagraficaOcrConfirmFieldPayload {
    const chosen = resolveOcrChosen(field, formValue);
    if (chosen === 'manual') {
        return {
            field_id: field.fieldId,
            chosen,
            value: String(formValue),
        };
    }
    return {
        field_id: field.fieldId,
        chosen,
    };
}

/**
 * 把 parse 结果收成前端核对会话，并丢掉不予填充的字段。
 * @param result 上传解析响应
 */
export function buildOcrReviewSession(result: AnagraficaOcrParseResult): AnagraficaOcrReviewSession {
    const fields = (result.fields ?? [])
        .filter((field) => isRestaurantOcrFillableKey(field.field_id))
        .map((field) => toReviewField(field));
    return {
        sessionId: result.session_id,
        fileName: result.file_name || '',
        sameAddr: result.same_addr !== false,
        confirmHighArmed: false,
        batchFieldIds: [],
        fields,
        activeFieldId: null,
    };
}

/**
 * 按三态把识别结果写入餐厅表单副本。
 * fill 写入建议值；match 仅在表单为空时回填现值；conflict 保留表单现值。
 * @param data 当前餐厅表单
 * @param session 核对会话
 */
export function applyOcrValues<T extends Record<string, unknown>>(
    data: T,
    session: AnagraficaOcrReviewSession,
): T {
    const next: T = { ...data };
    session.fields.forEach((field) => {
        if (field.status === 'fill') {
            next[field.fieldId as keyof T] = (field.valueConfirmed || field.valueOcr) as T[keyof T];
            return;
        }
        if (field.status === 'match' && String(next[field.fieldId as keyof T] ?? '') === '') {
            next[field.fieldId as keyof T] = (
                field.currentValue || field.valueConfirmed || field.valueOcr
            ) as T[keyof T];
        }
    });
    return next;
}

/**
 * 统计已核对与待核对数量。
 * @param session 核对会话；没有会话时总数为 0
 */
export function countOcrReview(session: AnagraficaOcrReviewSession | null): {
    total: number;
    confirmed: number;
    pending: number;
} {
    if (!session) {
        return { total: 0, confirmed: 0, pending: 0 };
    }
    const confirmed = session.fields.filter((field) => field.confirmed).length;
    return {
        total: session.fields.length,
        confirmed,
        pending: session.fields.length - confirmed,
    };
}

/**
 * 全部识别字段都已核对后才允许保存。没有识别字段时不额外阻断。
 * @param session 核对会话
 */
export function canSaveOcrReview(session: AnagraficaOcrReviewSession | null): boolean {
    return countOcrReview(session).pending === 0;
}

/**
 * 返回会话中第一个尚未核对的识别字段 id（按 fields 顺序）。
 * 无待核对项时返回 null。
 * @param session 核对会话
 */
export function getFirstPendingOcrFieldId(
    session: AnagraficaOcrReviewSession | null,
): string | null {
    if (!session) {
        return null;
    }
    /** 按识别字段顺序找首个未核对项，供保存阻断时滚表单定位。 */
    const pending = session.fields.find((field) => !field.confirmed);
    return pending?.fieldId ?? null;
}

/**
 * 确认所有未改写的高置信且非冲突字段。
 * 已改写的高置信项跳过，其余仍进入批量包，保存时走 confirm-high。
 * @param session 核对会话
 */
export function armOcrConfirmHigh(session: AnagraficaOcrReviewSession): AnagraficaOcrReviewSession {
    const batchFieldIds: string[] = [];
    const fields = session.fields.map((field) => {
        const eligible = field.level === 'high' && field.status !== 'conflict' && !field.modified;
        if (!eligible) {
            return field;
        }
        batchFieldIds.push(field.fieldId);
        const adoptedValue = field.status === 'match' ? field.currentValue : field.valueOcr;
        return {
            ...field,
            confirmed: true,
            confirmedByBatch: true,
            confirmedManually: false,
            chosen: resolveOcrChosen(field, adoptedValue),
        };
    });
    return {
        ...session,
        confirmHighArmed: batchFieldIds.length > 0,
        batchFieldIds,
        fields,
    };
}

/**
 * 撤销批量确认，使这批字段回到待核对。
 * @param session 核对会话
 */
export function disarmOcrConfirmHigh(session: AnagraficaOcrReviewSession): AnagraficaOcrReviewSession {
    const batchFieldIds = new Set(session.batchFieldIds);
    return {
        ...session,
        confirmHighArmed: false,
        batchFieldIds: [],
        fields: session.fields.map((field) => (
            batchFieldIds.has(field.fieldId)
                ? {
                    ...field,
                    confirmed: false,
                    confirmedByBatch: false,
                    confirmedManually: false,
                    chosen: null,
                }
                : field
        )),
    };
}

/**
 * 点一次确认高置信项，再点一次撤销。
 * @param session 核对会话
 */
export function toggleOcrConfirmHigh(session: AnagraficaOcrReviewSession): AnagraficaOcrReviewSession {
    if (session.confirmHighArmed) {
        return disarmOcrConfirmHigh(session);
    }
    return armOcrConfirmHigh(session);
}

/**
 * 用户改写识别字段。表单值与识别值相同时不视为改写。
 * 若改的是批量确认中的字段，只从批量包摘掉该项。
 * @param session 核对会话
 * @param fieldId 被改写的餐厅字段
 * @param formValue 当前表单文本
 */
export function markOcrFieldEdited(
    session: AnagraficaOcrReviewSession,
    fieldId: string,
    formValue: string,
): AnagraficaOcrReviewSession {
    const target = session.fields.find((field) => field.fieldId === fieldId);
    if (!target) {
        return session;
    }
    /** 与识别原值比较时去掉首尾空白，避免同步回写误标已修改。 */
    const normalizedForm = String(formValue).trim();
    const normalizedOcr = String(target.valueOcr).trim();
    if (normalizedForm === normalizedOcr) {
        if (!target.modified) {
            return session;
        }
        // 改回识别值：只清除已修改，保留待核对（需用户再确认）
        return {
            ...session,
            fields: session.fields.map((field) => (
                field.fieldId === fieldId
                    ? { ...field, modified: false }
                    : field
            )),
        };
    }
    /** 改写的是否仍在有效批量包内。 */
    const wasInBatch = session.confirmHighArmed && session.batchFieldIds.includes(fieldId);
    /** 摘掉本字段后剩余的批量字段。 */
    const nextBatchFieldIds = wasInBatch
        ? session.batchFieldIds.filter((id) => id !== fieldId)
        : session.batchFieldIds;
    return {
        ...session,
        confirmHighArmed: wasInBatch ? nextBatchFieldIds.length > 0 : session.confirmHighArmed,
        batchFieldIds: wasInBatch ? nextBatchFieldIds : session.batchFieldIds,
        fields: session.fields.map((field) => (
            field.fieldId === fieldId
                ? {
                    ...field,
                    confirmed: false,
                    modified: true,
                    confirmedByBatch: false,
                    confirmedManually: false,
                    chosen: null,
                }
                : field
        )),
    };
}

/**
 * 逐项切换核对状态：未核对 → 已核对；已核对再点 → 待核对（对齐原型 seal 切换）。
 * @param session 核对会话
 * @param fieldId 字段名
 * @param formValue 当前表单文本（确认时用于计算 chosen）
 */
export function confirmOcrField(
    session: AnagraficaOcrReviewSession,
    fieldId: string,
    formValue: string,
): AnagraficaOcrReviewSession {
    return {
        ...session,
        fields: session.fields.map((field) => {
            if (field.fieldId !== fieldId) {
                return field;
            }
            // 再次点击印章取消核对
            if (field.confirmed) {
                return {
                    ...field,
                    confirmed: false,
                    confirmedByBatch: false,
                    confirmedManually: false,
                    chosen: null,
                };
            }
            return {
                ...field,
                confirmed: true,
                confirmedByBatch: false,
                confirmedManually: true,
                chosen: resolveOcrChosen(field, formValue),
            };
        }),
    };
}

/**
 * 冲突项采用识别值，并标为已核对。
 * @param session 核对会话
 * @param fieldId 字段名
 */
export function adoptOcrFieldValue(
    session: AnagraficaOcrReviewSession,
    fieldId: string,
): { session: AnagraficaOcrReviewSession; value: string } {
    const field = session.fields.find((item) => item.fieldId === fieldId);
    const value = field?.valueOcr ?? '';
    const cleared = {
        ...session,
        fields: session.fields.map((item) => (
            item.fieldId === fieldId ? { ...item, modified: false } : item
        )),
    };
    return {
        session: confirmOcrField(cleared, fieldId, value),
        value,
    };
}

/**
 * 生成保存时的确认请求。
 * confirm-high 覆盖仍有效的批量高置信；confirm 只带用户手动核对项。
 * @param session 核对会话
 * @param formValues 当前表单，用于计算 chosen
 */
export function buildOcrSavePlan(
    session: AnagraficaOcrReviewSession,
    formValues: Record<string, unknown>,
): AnagraficaOcrSavePlan {
    return {
        callConfirmHigh: session.confirmHighArmed,
        fields: session.fields
            .filter((field) => field.confirmed && field.confirmedManually)
            .map((field) => buildOcrConfirmFieldPayload(
                field,
                String(formValues[field.fieldId] ?? ''),
            )),
    };
}

/**
 * 把接口字段转成核对字段。
 * @param field parse 返回的字段
 */
function toReviewField(field: AnagraficaOcrApiField): AnagraficaOcrReviewField {
    const valueOcr = field.value_ocr ?? '';
    const currentValue = field.current_value ?? '';
    const status = normalizeStatus(field.status, valueOcr, currentValue);
    const confidence = typeof field.confidence === 'number' ? field.confidence : 0;
    const confirmed = status === 'match' || field.confirmed === true;
    return {
        fieldId: field.field_id,
        sourceField: field.source_field ?? '',
        valueOcr,
        valueConfirmed: field.value_confirmed ?? valueOcr,
        currentValue,
        confidence,
        level: resolveOcrConfidenceLevel(confidence, field.confidence_level),
        status,
        page: field.page || 1,
        bbox: field.bbox ?? null,
        confirmed,
        modified: false,
        confirmedByBatch: false,
        confirmedManually: false,
        chosen: confirmed ? (status === 'match' ? 'current' : 'ocr') : null,
    };
}

/**
 * 归一化三态；未知状态按空现值、相等关系推断。
 * @param status 接口状态
 * @param valueOcr 识别值
 * @param currentValue 系统现值
 */
function normalizeStatus(
    status: string | undefined,
    valueOcr: string,
    currentValue: string,
): AnagraficaOcrFieldStatus {
    if (status === 'fill' || status === 'match' || status === 'conflict') {
        return status;
    }
    if (currentValue === valueOcr) {
        return 'match';
    }
    if (!currentValue) {
        return 'fill';
    }
    return 'conflict';
}

/** 餐厅入库后的 OCR 字段级留痕（挂在主体 `ocr_audit.fields`）。 */
export interface AnagraficaOcrAuditField {
    /** 识别原值（不可变）。 */
    value_ocr?: string;
    /** 人工确认后的入库值。 */
    value_confirmed?: string;
    /** 核对人账号 _id。 */
    confirmed_by?: string;
    /** 核对时间 ISO 字符串。 */
    confirmed_at?: string;
    /** 识别置信度 0–1。 */
    confidence?: number;
    /** 采纳侧：ocr / current / manual。 */
    chosen?: AnagraficaOcrChosen;
}

/** 列表/详情中的创建人或核对人摘要。 */
export interface AnagraficaOcrCreatedByUser {
    /** 账号 _id。 */
    _id?: string;
    /** 名。 */
    first_name?: string;
    /** 姓。 */
    last_name?: string;
}

/** 餐厅/集团主体上的 OCR 入库留痕。 */
export interface AnagraficaOcrAudit {
    /** 对应 OCR 会话 id。 */
    session_id?: string;
    /** 文件类型，如 NOTA_SIMPLE。 */
    document_type?: string;
    /** 签发国家，如 ES。 */
    country?: string;
    /** 主体范围。 */
    scope?: 'restaurant' | 'group';
    /** 税务地址与营业地址是否视为同一套。 */
    same_addr?: boolean;
    /** 核对人摘要（入库后顶层对象）。 */
    confirmed_by?: AnagraficaOcrCreatedByUser;
    /** 字段名 → 留痕。 */
    fields?: Record<string, AnagraficaOcrAuditField>;
}

/** `GET /v1/anagrafica/ocr` 单条会话（与 parse/get 同形，列表含完整 fields）。 */
export interface AnagraficaOcrSessionListItem {
    /** 会话 id。 */
    session_id: string;
    /** 主体范围。 */
    scope?: 'restaurant' | 'group';
    /** 已有主体 _id；新建通常为空。 */
    target_id?: string;
    /** 主体名称（餐厅/集团 nickname）。 */
    nickname?: string;
    /** 主体标识码。 */
    string_id?: string;
    /** 文件类型。 */
    document_type?: string;
    /** 签发国家。 */
    country?: string;
    /** 上传文件名。 */
    file_name?: string;
    /** 税务地址与营业地址是否视为同一套。 */
    same_addr?: boolean;
    /** 会话状态。 */
    status?: string;
    /** 未确认字段数。 */
    unconfirmed_count?: number;
    /** 字段总数。 */
    field_count?: number;
    /** 创建人账号 _id。 */
    created_by?: string;
    /** 创建人姓名摘要。 */
    created_by_user?: AnagraficaOcrCreatedByUser;
    /** 创建时间。 */
    created_at?: string;
    /** 最近更新时间。 */
    updated_at?: string;
    /** 识别字段列表。 */
    fields?: AnagraficaOcrApiField[];
}

/** 会话列表分页响应。 */
export interface AnagraficaOcrSessionListResult {
    /** 总条数。 */
    total: number;
    /** 当前页，从 1 起。 */
    page: number;
    /** 每页条数。 */
    page_size: number;
    /** 当前页会话。 */
    items: AnagraficaOcrSessionListItem[];
}

/** 列表查询参数。 */
export interface AnagraficaOcrListQuery {
    /** 可选，group 或 restaurant。 */
    scope?: 'group' | 'restaurant';
    /** 可选，已有主体 _id。 */
    target_id?: string;
    /** 可选，创建人账号 _id。 */
    created_by?: string;
    /** 可选，按主体名称模糊/精确搜索（以后端为准）。 */
    nickname?: string;
    /** 可选，按主体标识码搜索。 */
    string_id?: string;
    /** 页码，默认 1。 */
    page?: number;
    /** 每页条数，默认 20，最大 100。 */
    page_size?: number;
}

/**
 * 建档字段名 → 表单 i18n key（用于审计弹窗列「字段」）。
 * 地址类字段另在展示时加「营业/税务」前缀消歧。
 */
export const OCR_AUDIT_FIELD_LABEL_KEYS: Record<string, string> = {
    nickname: 'restaurant_info_page_text_restaurant_name',
    reg_name: 'restaurant_info_page_text_company',
    reg_fiscal_code: 'restaurant_info_page_text_fiscal_code',
    reg_vat_number: 'restaurant_info_page_text_vat_number',
    reg_pec: 'restaurant_info_page_text_PEC',
    reg_des_code: 'restaurant_info_page_text_client_recipient_code',
    reg_local: 'address_component_text_address',
    reg_postcode: 'restaurant_info_page_text_postcode',
    reg_city: 'restaurant_info_page_text_city',
    reg_state: 'restaurant_info_page_text_state',
    reg_country: 'restaurant_info_page_text_country',
    local: 'restaurant_info_page_text_restaurant_address',
    postcode: 'restaurant_info_page_text_postcode',
    city: 'restaurant_info_page_text_city',
    state: 'restaurant_info_page_text_state',
    country: 'restaurant_info_page_text_country',
    restaurant_contact_person: 'restaurant_info_page_text_contact_person',
};

/** 营业地址侧字段（与税务侧同名时需前缀）。 */
const BUSINESS_ADDRESS_FIELD_IDS = new Set([
    'local',
    'postcode',
    'city',
    'state',
    'country',
]);

/** 税务地址侧字段。 */
const TAX_ADDRESS_FIELD_IDS = new Set([
    'reg_local',
    'reg_postcode',
    'reg_city',
    'reg_state',
    'reg_country',
]);

/**
 * 判断入库留痕字段是否人工改过（R-C10「已修改」）。
 * 优先 `chosen === 'manual'`，否则比较识别原值与确认值。
 * @param field 留痕或会话字段片段
 */
export function isOcrAuditFieldModified(field: {
    chosen?: string | null;
    value_ocr?: string;
    value_confirmed?: string;
}): boolean {
    if (field.chosen === 'manual') {
        return true;
    }
    return String(field.value_ocr ?? '') !== String(field.value_confirmed ?? '');
}

/**
 * 解析字段展示名；双地址同义字段加营业/税务前缀。
 * @param fieldId 实体字段名
 * @param translate i18n.t 兼容函数
 */
export function resolveOcrAuditFieldLabel(
    fieldId: string,
    translate: (key: string) => string,
): string {
    const baseKey = OCR_AUDIT_FIELD_LABEL_KEYS[fieldId];
    const base = baseKey ? translate(baseKey) : fieldId;
    if (BUSINESS_ADDRESS_FIELD_IDS.has(fieldId)) {
        return `${translate('archive_form_business_address')} · ${base}`;
    }
    if (TAX_ADDRESS_FIELD_IDS.has(fieldId)) {
        return `${translate('archive_form_tax_address')} · ${base}`;
    }
    return base;
}

/**
 * 把 `ocr_audit.fields` 对象转成表格行（按字段名排序）。
 * @param fields 留痕字段字典
 */
export function flattenOcrAuditFields(
    fields: Record<string, AnagraficaOcrAuditField> | undefined,
): Array<AnagraficaOcrAuditField & { field_id: string }> {
    if (!fields) {
        return [];
    }
    return Object.keys(fields)
        .sort((a, b) => a.localeCompare(b))
        .map((fieldId) => ({
            field_id: fieldId,
            ...fields[fieldId],
        }));
}
